import {
  getWebInstrumentations,
  initializeFaro,
  faro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

const host = window.location.host;

const ENV = {
  isLocal: host.includes('local'),
  isStaging: host.includes('staging.'),
};

const CONFIG = {
  url: ENV.isStaging
    ? 'https://faro-collector-prod-us-east-2.grafana.net/collect/cd31155f2e557c5f2804a91858cd07ad'
    : 'https://faro-collector-prod-us-east-2.grafana.net/collect/9f8c28a9b3d7115c6fa951fef62122c6',
  appName: ENV.isStaging ? 'Highway-Broker-Staging' : 'Highway-Broker-Prod',
};

let initialized = false;
let pendingUser = null;

export function initializeGrafanaFaro() {
  if (ENV.isLocal || initialized) return;

  initializeFaro({
    url: CONFIG.url,
    app: {
      name: CONFIG.appName,
      version: process.env.VERSION,
      environment: process.env.NODE_ENV,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation(),
    ],
  });

  initialized = true;

  // Set pending user if setGrafanaFaroUser was called before initialization
  if (pendingUser) {
    setGrafanaFaroUser(pendingUser);
    pendingUser = null;
  }
}

export function initializeGrafanaFaroIfEnabled(featureFlags) {
  if (ENV.isLocal) return false;

  if (featureFlags?.enable_grafana_faro_broker?.enabled && !initialized) {
    initializeGrafanaFaro();
    return true;
  }
  return false;
}

export function setGrafanaFaroUser(user) {
  if (!user) return;

  // If Faro isn't initialized yet, store user to set after initialization
  if (!initialized) {
    pendingUser = user;
    return;
  }

  const userId = user.id?.toString();

  faro.api.setUser({
    id: userId,
    email: user.email,
    username: user.full_name || `${user.first_name} ${user.last_name}`.trim(),
    attributes: {
      companyId: user.company?.id?.toString(),
      companyName: user.company?.legal_name,
      companyType: user.company?.company_type,
    },
  });

  // Set user_id in session attributes for filtering
  const session = faro.api.getSession();
  faro.api.setSession({
    ...session,
    attributes: {
      ...session?.attributes,
      user_id: userId,
      company_id: user.company?.id?.toString(),
    },
  });
}

export function resetGrafanaFaroUser() {
  if (!initialized) return;
  faro.api.resetUser();
}

export function setGrafanaFaroView(viewName, attributes = {}) {
  if (!initialized) return;
  faro.api.setView({ name: viewName }, attributes);
}

export function pushGrafanaFaroEvent(name, attributes = {}) {
  if (!initialized) return;
  faro.api.pushEvent(name, attributes);
}

export function pushGrafanaFaroError(error, context = {}) {
  if (!initialized) return;
  faro.api.pushError(error, {
    context: {
      ...context,
      currentRoute: window.location.pathname,
    },
  });
}

export function setGrafanaFaroSessionAttributes(attributes) {
  if (!initialized) return;
  const session = faro.api.getSession();
  if (session) {
    faro.api.setSession({
      ...session,
      attributes: { ...session.attributes, ...attributes },
    });
  }
}

export function pushGrafanaFaroMeasurement(name, values, context = {}) {
  if (!initialized) return;
  faro.api.pushMeasurement({ type: name, values, context });
}

function getDefaultMeasurementContext() {
  if (typeof window === 'undefined') return {};
  return {
    route: window.location.pathname,
  };
}

/**
 * Emits Mapbox usage measurements to Faro for invoice validation.
 * These are meant to be aggregated (e.g. monthly) by measurement type and context fields.
 */
export function trackMapboxMapInit({ feature, style, context = {} } = {}) {
  pushGrafanaFaroMeasurement(
    'mapbox_map_init',
    { count: 1 },
    {
      ...getDefaultMeasurementContext(),
      feature,
      style,
      ...context,
    }
  );
}

export function trackMapboxStyleChange({ feature, style, context = {} } = {}) {
  pushGrafanaFaroMeasurement(
    'mapbox_style_change',
    { count: 1 },
    {
      ...getDefaultMeasurementContext(),
      feature,
      style,
      ...context,
    }
  );
}
